import { useState, useRef, useEffect, useCallback } from 'react'
import type { RiskAssessment } from '@/utils/health'
import type { Region } from '@/utils/time'
import Modal from './Modal'

// ── Region data ──

interface Helpline {
  name: string
  phone: string
  description: string
}

interface Resource {
  name: string
  url: string
  description: string
}

interface RegionInfo {
  label: string
  flag: string
  emergency: string
  doctorTitle: string
  doctorText: string
  guidelinesBody: string
  guidelinesBodyFull: string
  guidelinesWeekly: string
  guidelinesSession: string
  stdDrinkNote: string
  stdDrinkExamples: string[]
  helplines: Helpline[]
  resources: Resource[]
}

export const REGION_DATA: Record<Region, RegionInfo> = {
  AU: {
    label: 'Australia',
    flag: '\u{1F1E6}\u{1F1FA}',
    emergency: '000',
    doctorTitle: 'Talk to Your GP',
    doctorText: 'Your GP can provide confidential support and may refer you to a specialist. Medicare covers mental health care plans that include sessions with a psychologist.',
    guidelinesBody: 'NHMRC',
    guidelinesBodyFull: 'National Health and Medical Research Council',
    guidelinesWeekly: '10 standard drinks per week',
    guidelinesSession: '4 standard drinks on any single occasion',
    stdDrinkNote: 'A standard drink contains 10g of pure alcohol.',
    stdDrinkExamples: [
      '375ml mid-strength beer (3.5%)',
      '100ml wine (13%)',
      '30ml spirits (40%)',
    ],
    helplines: [
      { name: 'Lifeline', phone: '13 11 14', description: '24/7 crisis support and suicide prevention' },
      { name: 'Beyond Blue', phone: '1300 22 4636', description: 'Anxiety, depression and suicide prevention' },
      { name: 'National Alcohol and Other Drug Hotline', phone: '1800 250 015', description: 'Information, advice and referrals' },
    ],
    resources: [
      { name: 'DrinkWise Australia', url: 'https://drinkwise.org.au', description: 'Tools and resources for responsible drinking' },
      { name: 'Counselling Online', url: 'https://www.counsellingonline.org.au', description: 'Free counselling for alcohol and drug concerns' },
      { name: 'Hello Sunday Morning', url: 'https://www.hellosundaymorning.org', description: 'Community support to change your relationship with alcohol' },
      { name: 'SMART Recovery Australia', url: 'https://smartrecoveryaustralia.com.au', description: 'Self-empowering addiction recovery support groups' },
    ],
  },
  UK: {
    label: 'United Kingdom',
    flag: '\u{1F1EC}\u{1F1E7}',
    emergency: '999',
    doctorTitle: 'Talk to Your GP',
    doctorText: 'Your GP can provide confidential support and may refer you to local alcohol services. The NHS offers free treatment for alcohol problems.',
    guidelinesBody: 'UK CMO',
    guidelinesBodyFull: 'UK Chief Medical Officers',
    guidelinesWeekly: '14 units per week, spread over 3 or more days',
    guidelinesSession: 'no more than 6 units on a single occasion',
    stdDrinkNote: 'One UK unit contains 8g of pure alcohol (10ml).',
    stdDrinkExamples: [
      'A pint of lower-strength lager/beer (3.6%) = 2 units',
      'A 175ml glass of wine (12%) = 2.1 units',
      'A single 25ml measure of spirits (40%) = 1 unit',
    ],
    helplines: [
      { name: 'Drinkline', phone: '0300 123 1110', description: 'Free, confidential helpline for alcohol concerns' },
      { name: 'Samaritans', phone: '116 123', description: '24/7 emotional support, free from any phone' },
      { name: 'Alcohol Change UK', phone: '020 3907 8480', description: 'Advice and support to reduce drinking' },
    ],
    resources: [
      { name: 'NHS — Alcohol Support', url: 'https://www.nhs.uk/live-well/alcohol-advice', description: 'NHS guidance on alcohol and finding local services' },
      { name: 'Alcohol Change UK', url: 'https://alcoholchange.org.uk', description: 'Tools, research and support for changing drinking habits' },
      { name: 'SMART Recovery UK', url: 'https://smartrecovery.org.uk', description: 'Free, science-based mutual support groups' },
      { name: 'We Are With You', url: 'https://www.wearewithyou.org.uk', description: 'Free support for alcohol, drugs and mental health' },
    ],
  },
  US: {
    label: 'United States',
    flag: '\u{1F1FA}\u{1F1F8}',
    emergency: '911',
    doctorTitle: 'Talk to Your Doctor',
    doctorText: 'Your primary care provider can offer confidential support and referrals. Many insurance plans cover substance use disorder treatment.',
    guidelinesBody: 'NIAAA',
    guidelinesBodyFull: 'National Institute on Alcohol Abuse and Alcoholism',
    guidelinesWeekly: '14 drinks per week for men, 7 for women',
    guidelinesSession: '4 drinks on any single day for men, 3 for women',
    stdDrinkNote: 'A US standard drink contains 14g of pure alcohol.',
    stdDrinkExamples: [
      '12 fl oz of regular beer (5%)',
      '5 fl oz of wine (12%)',
      '1.5 fl oz of distilled spirits (40%)',
    ],
    helplines: [
      { name: 'SAMHSA National Helpline', phone: '1-800-662-4357', description: 'Free, confidential 24/7 referral and information' },
      { name: '988 Suicide & Crisis Lifeline', phone: '988', description: '24/7 crisis support via call or text' },
      { name: 'NIAAA Alcohol Treatment Navigator', phone: '1-800-662-4357', description: 'Help finding quality treatment near you' },
    ],
    resources: [
      { name: 'NIAAA Rethinking Drinking', url: 'https://www.rethinkingdrinking.niaaa.nih.gov', description: 'Tools and information to assess your drinking' },
      { name: 'AA (Alcoholics Anonymous)', url: 'https://www.aa.org', description: 'Find local meetings and support groups' },
      { name: 'SMART Recovery', url: 'https://www.smartrecovery.org', description: 'Science-based addiction recovery support' },
      { name: 'Moderation Management', url: 'https://moderation.org', description: 'Support for moderate drinking or abstinence' },
    ],
  },
  NZ: {
    label: 'New Zealand',
    flag: '\u{1F1F3}\u{1F1FF}',
    emergency: '111',
    doctorTitle: 'Talk to Your GP',
    doctorText: 'Your GP can provide confidential support and may refer you to community alcohol and drug services. Treatment is often free or subsidised.',
    guidelinesBody: 'NZ Ministry of Health',
    guidelinesBodyFull: 'New Zealand Ministry of Health',
    guidelinesWeekly: '10 standard drinks per week',
    guidelinesSession: '4 standard drinks on any single occasion',
    stdDrinkNote: 'A NZ standard drink contains 10g of pure alcohol.',
    stdDrinkExamples: [
      '330ml can of beer (4%)',
      '100ml glass of wine (12.5%)',
      '30ml shot of spirits (40%)',
    ],
    helplines: [
      { name: 'Alcohol Drug Helpline', phone: '0800 787 797', description: 'Free, confidential support 24/7' },
      { name: 'Need to Talk?', phone: '1737', description: 'Free call or text for mental health support' },
      { name: 'Lifeline Aotearoa', phone: '0800 543 354', description: '24/7 counselling and support' },
    ],
    resources: [
      { name: 'Health NZ — Alcohol', url: 'https://info.health.nz/keeping-healthy/addictions/alcohol', description: 'NZ health guidance on alcohol' },
      { name: 'Alcohol.org.nz', url: 'https://www.alcohol.org.nz', description: 'Tools and resources from the NZ Health Promotion Agency' },
      { name: 'AA New Zealand', url: 'https://www.aa.org.nz', description: 'Find local meetings and support groups' },
      { name: 'Community Alcohol and Drug Services', url: 'https://www.health.govt.nz/your-health/services-and-support/health-care-services/mental-health-services/alcohol-and-drug-services', description: 'Find local treatment services' },
    ],
  },
}

// ── Component ──

interface HealthDropdownProps {
  riskAssessment: RiskAssessment | null
  onDownloadReport: () => void
  region: Region
}

export default function HealthDropdown({ riskAssessment, onDownloadReport, region }: HealthDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [activeModal, setActiveModal] = useState<'alert' | 'healthy' | 'help' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const regionInfo = REGION_DATA[region]

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return
    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
        setFocusedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [dropdownOpen])

  // Keyboard navigation for dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!dropdownOpen) return

    if (e.key === 'Escape') {
      setDropdownOpen(false)
      setFocusedIndex(-1)
      buttonRef.current?.focus()
      e.preventDefault()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex(prev => {
        const next = prev < 2 ? prev + 1 : 0
        menuItemsRef.current[next]?.focus()
        return next
      })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(prev => {
        const next = prev > 0 ? prev - 1 : 2
        menuItemsRef.current[next]?.focus()
        return next
      })
    }
  }, [dropdownOpen])

  const handleButtonClick = () => {
    if (!riskAssessment) return

    if (riskAssessment.level === 'LOW') {
      setActiveModal('healthy')
    } else {
      setDropdownOpen(prev => !prev)
      setFocusedIndex(-1)
    }
  }

  const handleMenuItemClick = (action: 'alert' | 'report' | 'help') => {
    setDropdownOpen(false)
    setFocusedIndex(-1)
    if (action === 'alert') setActiveModal('alert')
    else if (action === 'report') onDownloadReport()
    else if (action === 'help') setActiveModal('help')
  }

  const isElevated = riskAssessment && (riskAssessment.level === 'INCREASED' || riskAssessment.level === 'HIGH')

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer ${!riskAssessment ? 'opacity-40 cursor-default' : ''}`}
        aria-label="Health status & resources"
        aria-haspopup={isElevated ? 'menu' : undefined}
        aria-expanded={isElevated ? dropdownOpen : undefined}
        data-tooltip="Health status"
      >
        {isElevated ? (
          // Outline heart with bold exclamation mark
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              stroke="var(--bac-danger)"
              strokeWidth="2"
            />
            <text x="12" y="12" textAnchor="middle" dominantBaseline="central" fill="var(--bac-danger)" fontSize="13" fontWeight="900">!</text>
          </svg>
        ) : (
          // Outline heart
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="var(--bac-danger)" strokeWidth="2">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            />
          </svg>
        )}
      </button>

      {/* Dropdown menu (INCREASED/HIGH only) */}
      {dropdownOpen && isElevated && (
        <div
          role="menu"
          aria-label="Health options"
          className="absolute right-0 top-full mt-1 w-52 bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg shadow-lg z-50 overflow-hidden"
          onKeyDown={handleKeyDown}
        >
          <button
            ref={el => { menuItemsRef.current[0] = el }}
            role="menuitem"
            tabIndex={focusedIndex === 0 ? 0 : -1}
            onClick={() => handleMenuItemClick('alert')}
            className="w-full text-left px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors cursor-pointer flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-[var(--bac-danger)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Health Alert!
          </button>
          <button
            ref={el => { menuItemsRef.current[1] = el }}
            role="menuitem"
            tabIndex={focusedIndex === 1 ? 0 : -1}
            onClick={() => handleMenuItemClick('report')}
            className="w-full text-left px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors cursor-pointer flex items-center gap-2 border-t border-[var(--border)]"
          >
            <svg className="w-4 h-4 text-[var(--text-secondary)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Doctor Note
          </button>
          <button
            ref={el => { menuItemsRef.current[2] = el }}
            role="menuitem"
            tabIndex={focusedIndex === 2 ? 0 : -1}
            onClick={() => handleMenuItemClick('help')}
            className="w-full text-left px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-colors cursor-pointer flex items-center gap-2 border-t border-[var(--border)]"
          >
            <svg className="w-4 h-4 text-[var(--accent-teal)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Further Help
          </button>
        </div>
      )}

      {/* Health Alert Modal */}
      <Modal
        isOpen={activeModal === 'alert'}
        onClose={() => setActiveModal(null)}
        title="Health Alert"
        size="lg"
        footer={
          <>
            <button onClick={() => { setActiveModal(null); onDownloadReport() }} className="btn btn-primary">
              Download Doctor Note
            </button>
            <button onClick={() => setActiveModal(null)} className="btn btn-secondary">
              Close
            </button>
          </>
        }
      >
        <HealthAlertContent riskAssessment={riskAssessment!} regionInfo={regionInfo} />
      </Modal>

      {/* Healthy Drinking Modal */}
      <Modal
        isOpen={activeModal === 'healthy'}
        onClose={() => setActiveModal(null)}
        title="Healthy Drinking"
        size="lg"
        footer={
          <button onClick={() => setActiveModal(null)} className="btn btn-secondary">
            Close
          </button>
        }
      >
        <HealthyDrinkingContent riskAssessment={riskAssessment!} regionInfo={regionInfo} />
      </Modal>

      {/* Further Help Modal */}
      <Modal
        isOpen={activeModal === 'help'}
        onClose={() => setActiveModal(null)}
        title="Further Help"
        size="lg"
        footer={
          <button onClick={() => setActiveModal(null)} className="btn btn-secondary">
            Close
          </button>
        }
      >
        <FurtherHelpContent
          region={region}
          regionInfo={regionInfo}
          onDownloadReport={() => { setActiveModal(null); onDownloadReport() }}
        />
      </Modal>
    </div>
  )
}

// ── Health Alert Modal Content ──

function HealthAlertContent({ riskAssessment, regionInfo }: { riskAssessment: RiskAssessment; regionInfo: RegionInfo }) {
  const isHigh = riskAssessment.level === 'HIGH'
  const bannerColor = isHigh ? 'var(--bac-danger)' : 'var(--accent-orange)'
  const levelLabel = isHigh ? 'HIGH RISK' : 'INCREASED RISK'

  return (
    <div className="space-y-5">
      {/* Risk banner */}
      <div className="rounded-lg px-4 py-3 text-center font-bold text-white" style={{ backgroundColor: bannerColor }}>
        {levelLabel}
      </div>

      {/* Why this alert was raised */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Why this alert was raised
        </h3>
        <div className="space-y-2 text-sm text-[var(--text-secondary)]">
          <div className="flex justify-between items-center">
            <span>Weekly avg standard drinks</span>
            <span className={`font-mono font-bold ${riskAssessment.weeklyAvgStdDrinks > 10 ? 'text-[var(--bac-danger)]' : 'text-[var(--text-primary)]'}`}>
              {riskAssessment.weeklyAvgStdDrinks.toFixed(1)}
              <span className="text-[var(--text-secondary)] font-normal ml-1">/ 10 guideline</span>
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Worst session std drinks</span>
            <span className={`font-mono font-bold ${riskAssessment.maxSessionStdDrinks > 4 ? 'text-[var(--bac-danger)]' : 'text-[var(--text-primary)]'}`}>
              {riskAssessment.maxSessionStdDrinks.toFixed(1)}
              <span className="text-[var(--text-secondary)] font-normal ml-1">/ 4 guideline</span>
            </span>
          </div>
        </div>
      </section>

      {/* What this means */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          What this means
        </h3>
        <div className="text-sm text-[var(--text-secondary)] space-y-2">
          <p>
            The {regionInfo.guidelinesBodyFull} ({regionInfo.guidelinesBody}) recommends no more than{' '}
            <strong className="text-[var(--text-primary)]">{regionInfo.guidelinesWeekly}</strong> and
            no more than <strong className="text-[var(--text-primary)]">{regionInfo.guidelinesSession}</strong> to
            reduce the risk of alcohol-related harm.
          </p>
          {isHigh ? (
            <p>
              At your current level, you are at substantially increased risk of alcohol-related disease,
              injury, and other harms including liver disease, cardiovascular problems, and mental health impacts.
            </p>
          ) : (
            <p>
              Your consumption exceeds these guidelines in at least one measure, which increases your
              risk of alcohol-related harm over time.
            </p>
          )}
        </div>
      </section>

      {/* What you can do */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          What you can do
        </h3>
        <ul className="text-sm text-[var(--text-secondary)] space-y-1 list-disc list-inside">
          <li>Keep tracking your drinks carefully to stay aware of your patterns</li>
          <li>Set a drink limit before each session and stick to it</li>
          <li>Talk to your doctor about strategies to reduce your intake</li>
          <li>Download your report to share with your doctor</li>
        </ul>
      </section>
    </div>
  )
}

// ── Healthy Drinking Modal Content ──

function HealthyDrinkingContent({ riskAssessment, regionInfo }: { riskAssessment: RiskAssessment; regionInfo: RegionInfo }) {
  return (
    <div className="space-y-5">
      {/* Low risk banner */}
      <div className="rounded-lg px-4 py-3 text-center font-bold text-white" style={{ backgroundColor: 'var(--bac-safe)' }}>
        LOW RISK
      </div>

      {/* Current pattern */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Your current pattern
        </h3>
        <div className="space-y-2 text-sm text-[var(--text-secondary)]">
          <div className="flex justify-between items-center">
            <span>Weekly avg standard drinks</span>
            <span className="font-mono font-bold text-[var(--bac-safe)]">
              {riskAssessment.weeklyAvgStdDrinks.toFixed(1)}
              <span className="text-[var(--text-secondary)] font-normal ml-1">/ 10 guideline</span>
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Worst session std drinks</span>
            <span className="font-mono font-bold text-[var(--bac-safe)]">
              {riskAssessment.maxSessionStdDrinks.toFixed(1)}
              <span className="text-[var(--text-secondary)] font-normal ml-1">/ 4 guideline</span>
            </span>
          </div>
        </div>
      </section>

      {/* What is moderate drinking */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          What is moderate drinking?
        </h3>
        <div className="text-sm text-[var(--text-secondary)] space-y-2">
          <p>
            The {regionInfo.guidelinesBody} recommends no more than{' '}
            <strong className="text-[var(--text-primary)]">{regionInfo.guidelinesWeekly}</strong> and
            no more than <strong className="text-[var(--text-primary)]">{regionInfo.guidelinesSession}</strong>.
          </p>
          <p>
            {regionInfo.stdDrinkNote} Examples:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            {regionInfo.stdDrinkExamples.map((example, i) => (
              <li key={i}>{example}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Warning signs */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Warning signs to watch for
        </h3>
        <ul className="text-sm text-[var(--text-secondary)] space-y-1 list-disc list-inside">
          <li>Drinking more than you planned</li>
          <li>Difficulty cutting down when you try</li>
          <li>Needing more alcohol for the same effect</li>
          <li>Drinking to cope with stress or emotions</li>
          <li>Others expressing concern about your drinking</li>
        </ul>
      </section>

      {/* Tips */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Tips for staying healthy
        </h3>
        <ul className="text-sm text-[var(--text-secondary)] space-y-1 list-disc list-inside">
          <li>Alternate alcoholic drinks with water or soft drinks</li>
          <li>Eat before and during drinking</li>
          <li>Set a limit before you start and stick to it</li>
          <li>Have alcohol-free days each week</li>
        </ul>
      </section>
    </div>
  )
}

// ── Further Help Modal Content ──

function FurtherHelpContent({ region, regionInfo, onDownloadReport }: { region: Region; regionInfo: RegionInfo; onDownloadReport: () => void }) {
  const [showOtherCountries, setShowOtherCountries] = useState(false)
  const otherRegions = (['AU', 'UK', 'US', 'NZ'] as Region[]).filter(r => r !== region)

  return (
    <div className="space-y-5">
      {/* Local Crisis Support */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Crisis Support ({regionInfo.label})
        </h3>
        <div className="space-y-2 text-sm">
          {regionInfo.helplines.map((h, i) => (
            <HelplineItem key={i} name={h.name} phone={h.phone} description={h.description} />
          ))}
        </div>
      </section>

      {/* Local Online Resources */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          Online Resources
        </h3>
        <div className="space-y-2 text-sm">
          {regionInfo.resources.map((r, i) => (
            <ResourceLink key={i} name={r.name} url={r.url} description={r.description} />
          ))}
        </div>
      </section>

      {/* Talk to Doctor */}
      <section>
        <h3 className="text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide mb-2">
          {regionInfo.doctorTitle}
        </h3>
        <div className="text-sm text-[var(--text-secondary)] space-y-2">
          <p>{regionInfo.doctorText}</p>
          <p>
            You can download your drinking report to bring to your appointment.
          </p>
          <button onClick={onDownloadReport} className="btn btn-primary text-sm mt-1">
            Download Doctor Note
          </button>
        </div>
      </section>

      {/* Other Countries (collapsible) */}
      <section>
        <button
          onClick={() => setShowOtherCountries(prev => !prev)}
          className="flex items-center gap-2 text-sm font-semibold text-[var(--accent-teal)] uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showOtherCountries ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Other Countries
        </button>

        {showOtherCountries && (
          <div className="mt-3 space-y-4">
            {otherRegions.map(r => {
              const other = REGION_DATA[r]
              return (
                <div key={r} className="border-l-2 border-[var(--border)] pl-3">
                  <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-2">
                    {other.label}
                  </h4>
                  <div className="space-y-2 text-sm">
                    {other.helplines.map((h, i) => (
                      <HelplineItem key={i} name={h.name} phone={h.phone} description={h.description} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Disclaimer */}
      <div className="text-xs text-[var(--text-secondary)] border-t border-[var(--border)] pt-3">
        This information is not a substitute for professional medical advice.
        If you are in immediate danger, call{' '}
        <strong className="text-[var(--text-primary)]">{regionInfo.emergency}</strong>.
      </div>
    </div>
  )
}

// ── Helper components ──

function HelplineItem({ name, phone, description }: { name: string; phone: string; description: string }) {
  return (
    <div className="flex items-start gap-3 text-[var(--text-secondary)]">
      <svg className="w-4 h-4 mt-0.5 text-[var(--accent-teal)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
      <div>
        <span className="font-medium text-[var(--text-primary)]">{name}</span>
        <span className="ml-2 font-mono text-[var(--accent-teal)]">{phone}</span>
        <p className="text-xs">{description}</p>
      </div>
    </div>
  )
}

function ResourceLink({ name, url, description }: { name: string; url: string; description: string }) {
  return (
    <div className="flex items-start gap-3 text-[var(--text-secondary)]">
      <svg className="w-4 h-4 mt-0.5 text-[var(--accent-teal)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
      <div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-[var(--accent-teal)] hover:underline"
        >
          {name}
          <span className="sr-only"> (opens in new tab)</span>
        </a>
        <p className="text-xs">{description}</p>
      </div>
    </div>
  )
}
